import type { ReactNode } from 'react'

import logoSvg from '@/assets/logo.svg'
import { useAuth } from '@/auth/AuthProvider'
import { FxAvatar, FxIcon } from '@/components/fx'
import { useUsage } from '@/hooks/useUsage'
import { cn } from '@/lib/utils'

/**
 * The four-step wizard chrome around the funnel: pick a form, find its
 * fields, read it, look at the result.
 *
 * It is a wrapper the three funnel pages render themselves rather than a
 * route element, because the step is a property of the page and deriving it
 * from the pathname would put a route table in here that the router already
 * owns. The pages stay flat in `CockpitApp`, and the result step — which may
 * yet move — needs no change here when it does.
 *
 * Deliberately thin: it owns the header and nothing else. No rail, no bottom
 * nav; somebody who has not signed up yet has nowhere to navigate to, and an
 * app shell around a funnel invites them to wander out of it.
 */

export const FUNNEL_STEPS = [
  { key: 'upload', label: 'Hochladen' },
  { key: 'detect', label: 'Felder erkennen' },
  { key: 'read', label: 'Auslesen' },
  { key: 'result', label: 'Ergebnis' },
] as const

export type FunnelStep = (typeof FUNNEL_STEPS)[number]['key']

export type FunnelStepState = 'done' | 'current' | 'upcoming'

/** The step as a key, or as the 1-based number the wizard puts in the circle. */
export type FunnelStepRef = FunnelStep | 1 | 2 | 3 | 4

/** Both spellings of "which step is this" onto the one the header works in. */
export function toFunnelStep(step: FunnelStepRef): FunnelStep {
  return typeof step === 'number' ? (FUNNEL_STEPS[step - 1] ?? FUNNEL_STEPS[0]).key : step
}

/**
 * Where each step stands relative to the one being shown. Split out because
 * it is the only thing in the header with a rule to get wrong, and a wrong
 * one is silent: the marker still renders, just on the wrong step.
 */
export function funnelStepStates(current: FunnelStep): FunnelStepState[] {
  const index = FUNNEL_STEPS.findIndex((step) => step.key === current)
  return FUNNEL_STEPS.map((_, i) =>
    i < index ? 'done' : i === index ? 'current' : 'upcoming',
  )
}

/** Initials for the header avatar, from whatever the profile actually has. */
export function initialsOf(user: { firstName?: string; lastName?: string; displayName?: string; email?: string } | null): string {
  if (!user) return ''
  const first = user.firstName?.trim()
  const last = user.lastName?.trim()
  if (first || last) return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase()
  const fallback = user.displayName?.trim() || user.email?.trim() || ''
  // A display name of two words gives two initials; an e-mail gives one.
  const parts = fallback.split(/[\s@.]+/).filter(Boolean)
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

/**
 * One step's marker: the number, or a check once the step is behind us.
 *
 * `FxAvatar` takes no children, so a step that has to show a glyph rather
 * than initials is drawn here against the same two tokens the avatar uses —
 * `primary-strong` filled, an outline in `fill` for a step not reached yet.
 */
function StepMarker({ state, number }: { state: FunnelStepState; number: number }) {
  if (state === 'done') {
    return (
      <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-primary-strong text-on-primary">
        <FxIcon name="check" size={18} />
      </span>
    )
  }
  return (
    <FxAvatar
      initials={String(number)}
      size={28}
      filled={state === 'current'}
      className={state === 'upcoming' ? 'border-fill text-text-muted' : 'font-semibold'}
    />
  )
}

export interface FunnelLayoutProps {
  /** Which of the four steps this page is, by key or by number. */
  step: FunnelStepRef
  /**
   * The right-hand slot. Left out it is the quota line, which is what steps
   * 1–3 want; the result step passes its "Zu meinen Formularen →" link.
   */
  right?: ReactNode
  children: ReactNode
}

export function FunnelLayout({ step, right, children }: FunnelLayoutProps) {
  const { user } = useAuth()
  const current = toFunnelStep(step)
  const states = funnelStepStates(current)
  const position = FUNNEL_STEPS.findIndex((entry) => entry.key === current) + 1
  const initials = initialsOf(user)

  return (
    <div className="min-h-screen bg-ground">
      <header className="sticky top-0 z-10 flex h-[72px] items-center gap-4 bg-surface px-5 shadow-raised sm:gap-6 sm:px-10">
        <span className="flex min-w-0 flex-none items-center gap-2 text-panel-title text-primary-text lg:w-[260px]">
          <img src={logoSvg} alt="" className="h-9 w-9 flex-none" />
          <span className="hidden xs:inline">formalogix</span>
        </span>

        {/* The full progress nav needs room for four labels; below `lg` the
            labels collapse to the current one, and below `md` the whole nav
            becomes the counter the phone artboard draws. Both forms are in
            the DOM only at their own width, so a reader never meets two. */}
        <nav
          aria-label="Fortschritt"
          className="hidden min-w-0 flex-1 md:flex md:justify-center"
        >
          <ol className="flex min-w-0 items-center gap-3 text-small">
            {FUNNEL_STEPS.map((entry, index) => {
              const state = states[index]
              return (
                <li
                  key={entry.key}
                  className="flex min-w-0 items-center gap-3"
                  aria-current={state === 'current' ? 'step' : undefined}
                >
                  {index > 0 && (
                    <span
                      aria-hidden
                      className={cn(
                        'h-[2px] w-6 flex-none lg:w-10',
                        state === 'upcoming' ? 'bg-fill' : 'bg-primary',
                      )}
                    />
                  )}
                  <span className="flex min-w-0 items-center gap-2">
                    <StepMarker state={state} number={index + 1} />
                    <span
                      className={cn(
                        'truncate',
                        state === 'current' ? 'font-semibold' : 'sr-only lg:not-sr-only',
                        state === 'upcoming' && 'text-text-muted',
                      )}
                    >
                      {entry.label}
                    </span>
                  </span>
                </li>
              )
            })}
          </ol>
        </nav>

        <span className="flex-1 text-small text-text-muted md:hidden">
          Schritt {position} von {FUNNEL_STEPS.length}
        </span>

        <span className="flex flex-none items-center justify-end gap-3 text-small lg:w-[260px]">
          {right === undefined ? <FunnelQuotaLine /> : right}
          {initials && <FxAvatar initials={initials} size={36} />}
        </span>
      </header>

      {children}
    </div>
  )
}

/**
 * The header's right-hand line on steps 1–3.
 *
 * Signed out there is no usage to read, and on a deployment that enforces no
 * limits there is no figure worth showing — both say the thing the funnel is
 * actually promising, which is that trying it costs nothing.
 */
export function FunnelQuotaLine() {
  const { signedIn } = useAuth()
  return signedIn ? <SignedInQuotaLine /> : <span>Der Test ist kostenlos</span>
}

function SignedInQuotaLine() {
  const usage = useUsage()
  const data = usage.data
  const pages = data?.usage

  if (!data?.enforced || !pages || pages.maxPages < 0 || pages.pagesRemaining == null) {
    return <span>Der Test ist kostenlos</span>
  }

  return (
    <span className="whitespace-nowrap">
      Noch {pages.pagesRemaining} von {pages.maxPages} Seiten
    </span>
  )
}
